import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ButtonWithIcon, CategoryIcon, CreateIcon, Grid, Spinner, actionIconStyles } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { Product, ProductAttributeDefinition, ProductCategory } from '../types/Product';
import { getProductCategories, getProducts, productService } from '../services/productService';
import { formatAttributeSummary } from '../../catalog/CatalogAttributeFilters';
import { CatalogFilterPanel } from '../../catalog/CatalogFilterPanel';
import { formatMoney } from '../../invoices/invoiceForm';
import { useTenantCurrency } from '../../invoices/tenantCurrency';
import styles from '../../catalog/Catalog.module.css';

type ProductView = 'table' | 'list' | 'gallery';

const VIEW_STORAGE_KEY = 'shahagent.products.view';

const parseView = (value: string | null): ProductView => {
  if (value === 'table' || value === 'list' || value === 'gallery') return value;
  return 'list';
};

const readStoredView = (): ProductView => {
  try {
    return parseView(localStorage.getItem(VIEW_STORAGE_KEY));
  } catch {
    return 'list';
  }
};

const ProductPhoto: React.FC<{ src?: string; alt: string; className: string; placeholderClassName: string }> = ({
  src,
  alt,
  className,
  placeholderClassName,
}) => (
  src
    ? <img className={className} src={src} alt={alt} />
    : (
      <div className={placeholderClassName} aria-hidden>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
          <path d="M4.75 7.5A2.75 2.75 0 0 1 7.5 4.75h9A2.75 2.75 0 0 1 19.25 7.5v9A2.75 2.75 0 0 1 16.5 19.25h-9A2.75 2.75 0 0 1 4.75 16.5v-9Z" stroke="currentColor" strokeWidth="1.6" />
          <path d="m8 14.5 2.2-2.2a1 1 0 0 1 1.4 0L15.5 16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <circle cx="9.25" cy="9.25" r="1.15" fill="currentColor" />
        </svg>
      </div>
    )
);

const StatusBadge: React.FC<{ status: Product['status']; archivedLabel: string; activeLabel: string }> = ({
  status,
  archivedLabel,
  activeLabel,
}) => (
  <span className={`${styles.statusBadge} ${status === 'archived' ? styles.statusArchived : styles.statusActive}`}>
    {status === 'archived' ? archivedLabel : activeLabel}
  </span>
);

export const Products: React.FC = () => {
  const { t } = useTranslation(['products', 'common']);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [items, setItems] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [status, setStatus] = useState('');
  const [definitions, setDefinitions] = useState<ProductAttributeDefinition[]>([]);
  const [attributeFilters, setAttributeFilters] = useState<Record<string, string>>({});
  const [view, setView] = useState<ProductView>(readStoredView);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const canCreate = hasPermission(PermissionScopes.productsCreate);
  const currency = useTenantCurrency();

  const activeAttributeFilters = useMemo(
    () => Object.fromEntries(Object.entries(attributeFilters).filter(([, value]) => value.trim())),
    [attributeFilters],
  );
  const attributeFilterKey = JSON.stringify(activeAttributeFilters);

  useEffect(() => {
    if (categoryIds.length !== 1) {
      setDefinitions([]);
      return;
    }

    const selectedCategoryId = categoryIds[0];
    let cancelled = false;
    void productService.getAttributes(selectedCategoryId, true)
      .then(items => {
        if (!cancelled) setDefinitions(items);
      })
      .catch(() => {
        if (!cancelled) setDefinitions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [categoryIds]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [products, productCategories] = await Promise.all([
          getProducts({
            page: 1,
            pageSize: 200,
            categoryIds: categoryIds.length > 0 ? categoryIds : undefined,
            status: status === 'active' || status === 'archived' ? status : undefined,
            attributes: Object.keys(activeAttributeFilters).length > 0 ? activeAttributeFilters : undefined,
          }),
          getProductCategories(),
        ]);
        setItems(products);
        setCategories(productCategories);
        setError(null);
      } catch {
        setError(t('failedToLoad', { ns: 'products' }));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [activeAttributeFilters, attributeFilterKey, categoryIds, status, t]);

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, view);
    } catch {
      // Ignore private-mode storage failures.
    }
  }, [view]);

  const filtered = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return items;
    return items.filter(item =>
      item.name.toLowerCase().includes(query)
      || (item.displayName || '').toLowerCase().includes(query)
      || (item.code || '').toLowerCase().includes(query)
      || (item.barcode || '').toLowerCase().includes(query)
      || item.categoryName.toLowerCase().includes(query)
      || Object.values(item.attributes).some(value => value.toLowerCase().includes(query)));
  }, [items, searchTerm]);

  const openProduct = (item: Product) => navigate(`/products/${item.id}`);

  const clearFilters = () => {
    setAttributeFilters({});
    setCategoryIds([]);
    setStatus('');
  };

  const views: Array<{ id: ProductView; label: string; icon: React.ReactNode }> = [
    {
      id: 'table',
      label: t('viewTable', { ns: 'products' }),
      icon: (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden>
          <path d="M5 6.5h14M5 12h14M5 17.5h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      id: 'list',
      label: t('viewList', { ns: 'products' }),
      icon: (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden>
          <rect x="4.25" y="5.25" width="4.5" height="4.5" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
          <path d="M11.5 6.5h8M11.5 8.75h5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <rect x="4.25" y="14.25" width="4.5" height="4.5" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
          <path d="M11.5 15.5h8M11.5 17.75h5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      id: 'gallery',
      label: t('viewGallery', { ns: 'products' }),
      icon: (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden>
          <rect x="4.25" y="4.25" width="6.5" height="6.5" rx="1.4" stroke="currentColor" strokeWidth="1.6" />
          <rect x="13.25" y="4.25" width="6.5" height="6.5" rx="1.4" stroke="currentColor" strokeWidth="1.6" />
          <rect x="4.25" y="13.25" width="6.5" height="6.5" rx="1.4" stroke="currentColor" strokeWidth="1.6" />
          <rect x="13.25" y="13.25" width="6.5" height="6.5" rx="1.4" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      ),
    },
  ];

  const resultLabel = filtered.length === 1
    ? t('resultCountOne', { ns: 'products' })
    : t('resultCount', { ns: 'products', count: filtered.length });

  return (
    <CatalogFilterPanel
      ns="products"
      title={t('title', { ns: 'products' })}
      headerActions={(
        <>
          <button
            className={`btn btn-secondary ${actionIconStyles.buttonWithIcon}`}
            type="button"
            onClick={() => navigate('/products/categories')}
          >
            <ButtonWithIcon icon={<CategoryIcon />}>{t('manageCategories', { ns: 'products' })}</ButtonWithIcon>
          </button>
          {canCreate ? (
            <button
              className={`btn btn-primary ${actionIconStyles.buttonWithIcon}`}
              type="button"
              onClick={() => navigate('/products/add')}
            >
              <ButtonWithIcon icon={<CreateIcon />}>{t('addProduct', { ns: 'products' })}</ButtonWithIcon>
            </button>
          ) : null}
        </>
      )}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={t('searchPlaceholder', { ns: 'products' })}
      resultLabel={loading ? undefined : resultLabel}
      toolbarEnd={(
        <div className={styles.viewToggle} role="group" aria-label={t('viewLabel', { ns: 'products' })}>
          {views.map(option => (
            <button
              key={option.id}
              type="button"
              className={`${styles.viewToggleButton} ${view === option.id ? styles.viewToggleButtonActive : ''}`}
              aria-pressed={view === option.id}
              aria-label={option.label}
              title={option.label}
              onClick={() => setView(option.id)}
            >
              {option.icon}
              <span className={styles.viewToggleLabel}>{option.label}</span>
            </button>
          ))}
        </div>
      )}
      open={isFilterOpen}
      onOpenChange={setIsFilterOpen}
      categories={categories}
      categoryIds={categoryIds}
      onCategoryIdsChange={nextCategoryIds => {
        setAttributeFilters({});
        setCategoryIds(nextCategoryIds);
      }}
      status={status}
      onStatusChange={setStatus}
      definitions={definitions}
      attributeFilters={attributeFilters}
      onAttributeChange={(attributeId, value) => setAttributeFilters(prev => ({ ...prev, [attributeId]: value }))}
      onClearFilters={clearFilters}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
          <Spinner text={t('loading', { ns: 'products' })} />
        </div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : filtered.length === 0 ? (
        <div className={styles.emptyState}>{t('empty', { ns: 'products' })}</div>
      ) : view === 'gallery' ? (
        <div className={styles.gallery}>
          {filtered.map(item => (
            <button
              key={item.id}
              type="button"
              className={`${styles.galleryCard} ${item.status === 'archived' ? styles.galleryCardMuted : ''}`}
              onClick={() => openProduct(item)}
            >
              <ProductPhoto
                src={item.imageUrl}
                alt=""
                className={styles.galleryPhoto}
                placeholderClassName={styles.galleryPlaceholder}
              />
              <div className={styles.galleryBadge}>
                <StatusBadge
                  status={item.status}
                  activeLabel={t('statusActive', { ns: 'products' })}
                  archivedLabel={t('statusArchived', { ns: 'products' })}
                />
              </div>
              <div className={styles.galleryBody}>
                <div className={styles.galleryTitle}>{item.displayName || item.name}</div>
                <div className={styles.galleryMeta}>
                  {formatAttributeSummary(definitions, item.attributes, t('yes', { ns: 'products' }), t('no', { ns: 'products' }))
                    || item.categoryName
                    || item.code
                    || '-'}
                </div>
                <div className={styles.galleryFooter}>
                  <span className={styles.galleryPrice}>{formatMoney(item.salePrice, currency)}</span>
                  <span className={styles.galleryStock}>
                    {item.stockQuantity}{item.unit ? ` ${item.unit}` : ''}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : view === 'list' ? (
        <div className={styles.comfortableList}>
          {filtered.map(item => (
            <button
              key={item.id}
              type="button"
              className={`${styles.comfortableRow} ${item.status === 'archived' ? styles.comfortableRowMuted : ''}`}
              onClick={() => openProduct(item)}
            >
              <ProductPhoto
                src={item.imageUrl}
                alt=""
                className={styles.comfortablePhoto}
                placeholderClassName={`${styles.comfortablePhoto} ${styles.comfortablePlaceholder}`}
              />
              <div className={styles.valueStack}>
                <span className={styles.primaryText}>{item.displayName || item.name}</span>
                <span className={styles.secondaryText}>
                  {formatAttributeSummary(definitions, item.attributes, t('yes', { ns: 'products' }), t('no', { ns: 'products' }))
                    || item.code
                    || item.barcode
                    || item.categoryName
                    || '-'}
                </span>
              </div>
              <span className={styles.comfortableCategory}>{item.categoryName || '-'}</span>
              <span className={styles.comfortablePrice}>{formatMoney(item.salePrice, currency)}</span>
              <span className={styles.comfortableStock}>
                {item.stockQuantity}{item.unit ? ` ${item.unit}` : ''}
              </span>
              <StatusBadge
                status={item.status}
                activeLabel={t('statusActive', { ns: 'products' })}
                archivedLabel={t('statusArchived', { ns: 'products' })}
              />
            </button>
          ))}
        </div>
      ) : (
        <Grid
          columns={[
            {
              key: 'photo',
              label: t('photo', { ns: 'products' }),
              className: styles.tablePhotoCol,
              headerClassName: styles.tablePhotoCol,
              render: (item: Product) => (
                <ProductPhoto
                  src={item.imageUrl}
                  alt=""
                  className={styles.listThumb}
                  placeholderClassName={`${styles.listThumb} ${styles.listThumbPlaceholder}`}
                />
              ),
            },
            {
              key: 'name',
              label: t('name', { ns: 'products' }),
              render: (item: Product) => (
                <div className={styles.valueStack}>
                  <span className={styles.primaryText}>{item.name}</span>
                  <span className={styles.secondaryText}>
                    {formatAttributeSummary(definitions, item.attributes, t('yes', { ns: 'products' }), t('no', { ns: 'products' }))
                      || item.code
                      || item.barcode
                      || '-'}
                  </span>
                </div>
              ),
            },
            {
              key: 'category',
              label: t('category', { ns: 'products' }),
              render: (item: Product) => <span className={styles.valueText}>{item.categoryName || '-'}</span>,
            },
            {
              key: 'salePrice',
              label: t('salePrice', { ns: 'products' }),
              render: (item: Product) => <span className={styles.valueText}>{formatMoney(item.salePrice, currency)}</span>,
            },
            {
              key: 'stock',
              label: t('stockQuantity', { ns: 'products' }),
              render: (item: Product) => <span className={styles.valueText}>{item.stockQuantity}</span>,
            },
            {
              key: 'status',
              label: t('status', { ns: 'products' }),
              render: (item: Product) => (
                <StatusBadge
                  status={item.status}
                  activeLabel={t('statusActive', { ns: 'products' })}
                  archivedLabel={t('statusArchived', { ns: 'products' })}
                />
              ),
            },
          ]}
          rows={filtered}
          rowKey={(item: Product) => item.id}
          onRowClick={openProduct}
        />
      )}
    </CatalogFilterPanel>
  );
};

export default Products;
